import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import { X, User, Ticket, Package, Bell, Lifebuoy, FileText, ShieldCheck, Info, SignOut, SignIn, UserPlus, Car, SteeringWheel, Gauge, CaretDown } from "@phosphor-icons/react";
import { useAuth } from "../lib/auth";

const CLIENT_ONLY = ["/reservations", "/mes-colis"];

const ITEMS = [
  { to: "/compte", label: "Mon compte", icon: User, auth: true },
  { to: "/reservations", label: "Mes réservations", icon: Ticket, auth: true },
  { to: "/mes-colis", label: "Mes colis", icon: Package, auth: true },
  { to: "/notifications", label: "Notifications", icon: Bell, auth: true },
  { to: "/aide", label: "Centre d'aide", icon: Lifebuoy },
  { to: "/conditions", label: "Conditions d'utilisation", icon: FileText },
  { to: "/confidentialite", label: "Confidentialité", icon: ShieldCheck },
  { to: "/a-propos", label: "À propos", icon: Info },
];

function MenuLink({ to, label, icon: Icon, onClick, testid }) {
  return (
    <Link to={to} onClick={onClick} data-testid={testid}
      className="flex items-center gap-3 px-3 py-3 rounded-xl text-[15px] font-medium text-[#1A1F1C] hover:bg-white active:bg-white transition-colors">
      <Icon size={20} weight="duotone" className="text-[#1E3F33]" />
      {label}
    </Link>
  );
}

// Chauffeur : les écrans client sont rangés sous « Utiliser comme client »
function ClientSection({ onClose }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} data-testid="menu-client-toggle"
        className="w-full flex items-center gap-3 px-3 py-3 rounded-xl text-[15px] font-medium text-[#1A1F1C] hover:bg-white transition-colors">
        <User size={20} weight="duotone" className="text-[#C85A17]" />
        <span className="flex-1 text-left">Utiliser comme client</span>
        <CaretDown size={16} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="pl-4" data-testid="menu-client-items">
          <MenuLink to="/location" label="Louer une voiture" icon={Car} onClick={onClose} testid="menu-location" />
          <MenuLink to="/reservations" label="Mes réservations" icon={Ticket} onClick={onClose} testid="menu-reservations" />
          <MenuLink to="/mes-colis" label="Mes colis" icon={Package} onClick={onClose} testid="menu-mes-colis" />
        </div>
      )}
    </div>
  );
}

export default function SideMenu({ open, onClose }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  if (!open) return null;

  const isDriver = user?.role === "driver";
  const items = ITEMS.filter((i) => (!i.auth || user) && !(isDriver && CLIENT_ONLY.includes(i.to)));
  const roleLink = user?.role === "admin"
    ? { to: "/admin", label: "Administration", icon: Gauge }
    : user?.role === "driver" ? { to: "/chauffeur", label: "Espace chauffeur", icon: SteeringWheel } : null;

  const doLogout = async () => { await logout(); onClose(); navigate("/"); };

  // Portal : le header a un backdrop-filter qui piègerait un élément `fixed` à l'intérieur
  return createPortal(
    <div className="fixed inset-0 z-[60] md:hidden" data-testid="side-menu">
      <button className="absolute inset-0 bg-black/40" onClick={onClose} aria-label="Fermer le menu" data-testid="side-menu-backdrop" />
      <aside className="absolute right-0 top-0 h-full w-[84%] max-w-sm bg-[#F9F6F0] shadow-2xl flex flex-col safe-top safe-bottom">
        <div className="flex items-center justify-between px-4 h-16 border-b border-[#E8E5DF]">
          <div>
            <div className="font-display font-bold text-base text-[#1A1F1C]">{user ? user.name : "Menu"}</div>
            {user && <div className="text-xs text-[#5C6B64] truncate max-w-[200px]">{user.email}</div>}
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-white" aria-label="Fermer" data-testid="side-menu-close"><X size={22} /></button>
        </div>

        <nav className="flex-1 px-3 py-3 flex flex-col gap-0.5">
          {roleLink && <MenuLink {...roleLink} onClick={onClose} testid="menu-role-link" />}
          {!isDriver && <MenuLink to="/location" label="Louer une voiture" icon={Car} onClick={onClose} testid="menu-location" />}
          {items.map((i) => (
            <MenuLink key={i.to} {...i} onClick={onClose} testid={`menu-${i.to.replace(/\//g, "") || "home"}`} />
          ))}
          {isDriver && <ClientSection onClose={onClose} />}
        </nav>

        <div className="px-3 pb-4 pt-2 border-t border-[#E8E5DF]">
          {user ? (
            <button onClick={doLogout} data-testid="menu-logout"
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-semibold text-[#D94E4E] bg-[#D94E4E]/10 hover:bg-[#D94E4E]/15 transition-colors">
              <SignOut size={20} /> Déconnexion
            </button>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              <Link to="/login" onClick={onClose} className="btn-outline justify-center" data-testid="menu-login"><SignIn size={18} /> Connexion</Link>
              <Link to="/register" onClick={onClose} className="btn-primary justify-center" data-testid="menu-register"><UserPlus size={18} /> S'inscrire</Link>
            </div>
          )}
        </div>
      </aside>
    </div>,
    document.body
  );
}
